import { useState } from 'react';
import type { Property, PropertyCategory } from '../types';
import { CATEGORY_LABELS } from '../types';
import * as db from '../lib/db';
import { useAppData } from '../store';

export default function PropertyForm({
  existing,
  onDone,
  onCancel,
}: {
  existing?: Property;
  onDone: (property: Property) => void;
  onCancel: () => void;
}) {
  const { refresh } = useAppData();
  const [name, setName] = useState(existing?.name ?? '');
  const [category, setCategory] = useState<PropertyCategory>(existing?.category ?? 'property');
  const [llcName, setLlcName] = useState(existing?.llc_name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [location, setLocation] = useState(existing?.location ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        category,
        llc_name: llcName.trim() || null,
        description: description.trim() || null,
        location: location.trim() || null,
        notes: notes.trim() || null,
      };
      const property = existing
        ? await db.updateProperty(existing.id, payload)
        : await db.createProperty(payload);
      await refresh();
      onDone(property);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="form card" onSubmit={handleSubmit}>
      <h2>{existing ? 'Edit property' : 'Add a property'}</h2>

      <label>
        Name
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. 253 Town Farm Hill Road"
          required
          autoFocus
        />
      </label>

      <label>
        Category
        <select value={category} onChange={(e) => setCategory(e.target.value as PropertyCategory)}>
          {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>

      <label>
        LLC (optional)
        <input
          value={llcName}
          onChange={(e) => setLlcName(e.target.value)}
          placeholder="e.g. Red Barn LLC"
        />
      </label>

      <label>
        Description (optional)
        <input value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>

      <label>
        Location (optional)
        <input value={location} onChange={(e) => setLocation(e.target.value)} />
      </label>

      <label>
        Notes
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
      </label>

      {error && <p className="error">{error}</p>}

      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}
